import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { rupiah, formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Wallet, Send, CheckCircle2, Plus, ReceiptText } from "lucide-react";

export default function Disbursements() {
  const [payouts, setPayouts] = useState([]);
  const [talents, setTalents] = useState([]);
  const [meta, setMeta] = useState({ easylink_live: false });
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [form, setForm] = useState({ talent_id: "", amount: "", note: "" });

  const load = () => {
    const url = filter === "all" ? "/payouts" : `/payouts?status=${filter}`;
    api.get(url).then((res) => setPayouts(res.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    api.get("/talents").then((res) => setTalents(res.data.filter((t) => t.status === "active"))).catch(() => {});
    api.get("/meta").then((res) => setMeta(res.data)).catch(() => {});
  }, []);

  const createPayout = async () => {
    if (!form.talent_id || !form.amount) { toast.error("Pilih talent & isi jumlah"); return; }
    try {
      await api.post("/payouts/request", { talent_id: form.talent_id, amount: Number(form.amount), note: form.note });
      toast.success("Permintaan payout dibuat (status Pending)");
      setOpen(false);
      setForm({ talent_id: "", amount: "", note: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const disburse = async (p) => {
    try {
      const { data } = await api.post(`/payouts/${p.id}/disburse`);
      const ref = data.tarapay_ref || data.easylink_ref;
      toast.success(`Disburse ${data.gateway_mode === "simulation" ? "(sandbox)" : ""} — ${ref}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const settle = async (p) => {
    try {
      const { data } = await api.post(`/payouts/${p.id}/settle`);
      setReceipt(data);
      toast.success("Payout selesai (Paid). Bukti transfer siap.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const filters = ["all", "pending", "processing", "paid", "failed"];
  const isLive = meta.tarapay_live || meta.easylink_live;

  return (
    <div className="space-y-6" data-testid="admin-disbursement-container">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Disbursement Komisi</h1>
          <p className="text-muted-foreground mt-1 flex items-center gap-2">
            Payout ke talent via Payment Gateway
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${isLive ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-amber-500/15 text-amber-400 border-amber-500/30"}`}>
              {isLive ? "LIVE" : "SANDBOX / SIMULASI"}
            </span>
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-payout-button" className="bg-primary hover:bg-primary/90 text-white">
              <Plus className="h-4 w-4 mr-2" /> Payout Baru
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2"><Wallet className="h-5 w-5 text-primary" /> Buat Payout</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Talent</Label>
                <Select value={form.talent_id} onValueChange={(v) => setForm({ ...form, talent_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="payout-talent-select"><SelectValue placeholder="Pilih talent" /></SelectTrigger>
                  <SelectContent>
                    {talents.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} — tersedia {rupiah(t.balance?.available)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Jumlah (Rp)</Label>
                <Input data-testid="payout-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label>Catatan</Label>
                <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="payout-create-submit" onClick={createPayout} className="bg-primary hover:bg-primary/90 text-white w-full">Buat Permintaan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-2 flex-wrap">
        {filters.map((f) => (
          <button
            key={f}
            data-testid={`filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-colors ${filter === f ? "bg-primary text-white" : "bg-card text-muted-foreground hover:text-foreground border border-border"}`}
          >
            {f === "all" ? "Semua" : f}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payouts-table">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="px-4 py-3 font-medium">Tanggal</th>
                <th className="px-4 py-3 font-medium">Talent</th>
                <th className="px-4 py-3 font-medium">Tujuan</th>
                <th className="px-4 py-3 font-medium text-right">Jumlah</th>
                <th className="px-4 py-3 font-medium text-right">Fee</th>
                <th className="px-4 py-3 font-medium text-right">Diterima</th>
                <th className="px-4 py-3 font-medium">Ref</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id} className="border-b border-border/50 hover:bg-secondary/40 transition-colors" data-testid={`payout-row-${p.id}`}>
                  <td className="px-4 py-3 text-muted-foreground">{formatDate(p.created_at)}</td>
                  <td className="px-4 py-3 font-medium">{p.talent_name}</td>
                  <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{p.bank_name}<br />{p.account_number}</td>
                  <td className="px-4 py-3 text-right font-mono">{rupiah(p.amount)}</td>
                  <td className="px-4 py-3 text-right font-mono text-muted-foreground">{rupiah(p.fee)}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-400">{rupiah(p.net_amount)}</td>
                  <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{p.tarapay_ref || p.easylink_ref || "-"}</td>
                  <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-3 text-right">
                    {p.status === "pending" && (
                      <Button size="sm" data-testid={`disburse-btn-${p.id}`} onClick={() => disburse(p)} className="bg-primary hover:bg-primary/90 text-white h-8">
                        <Send className="h-3.5 w-3.5 mr-1" /> Disburse
                      </Button>
                    )}
                    {p.status === "processing" && (
                      <Button size="sm" data-testid={`settle-btn-${p.id}`} onClick={() => settle(p)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8">
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Tandai Paid
                      </Button>
                    )}
                    {p.status === "paid" && (
                      <button data-testid={`receipt-btn-${p.id}`} onClick={() => setReceipt(p)} className="text-emerald-400 hover:text-emerald-300 inline-flex items-center text-xs">
                        <ReceiptText className="h-4 w-4 mr-1" /> Bukti
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {payouts.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">Belum ada payout.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!receipt} onOpenChange={(v) => !v && setReceipt(null)}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="receipt-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><ReceiptText className="h-5 w-5 text-emerald-400" /> Bukti Transfer</DialogTitle>
          </DialogHeader>
          {receipt && (
            <div className="space-y-3 text-sm">
              <div className="text-center py-4 border-b border-border">
                <div className="h-14 w-14 rounded-full bg-emerald-500/15 grid place-items-center mx-auto mb-2">
                  <CheckCircle2 className="h-7 w-7 text-emerald-400" />
                </div>
                <p className="font-display text-2xl font-bold text-emerald-400">{rupiah(receipt.net_amount)}</p>
                <p className="text-xs text-muted-foreground">Transfer Berhasil</p>
              </div>
              {[
                ["Talent", receipt.talent_name],
                ["Tujuan", `${receipt.bank_name} • ${receipt.account_number}`],
                ["Nama Penerima", receipt.account_holder],
                ["Jumlah Komisi", rupiah(receipt.amount)],
                ["Biaya Admin", rupiah(receipt.fee)],
                ["Ref", receipt.tarapay_ref || receipt.easylink_ref],
                ["Gateway", (receipt.gateway_mode || "").toUpperCase()],
                ["Waktu", formatDate(receipt.paid_at)],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="font-mono text-right">{v}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
